import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clientAPI, projectAPI, settingsAPI } from '../services/api';
import Layout from '../components/Layout';
import './ClientDetails.css';

const Icon = ({ name, size = 20, color = "currentColor" }) => {
  const icons = {
    arrowLeft: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>,
    user: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>,
    mail: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><polyline points="22,6 12,13 2,6" /></svg>,
    phone: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" /></svg>,
    mapPin: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></svg>,
    briefcase: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="3" ry="3" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>,
    currency: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" /><path d="M12 18V6" /></svg>,
    fileText: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>,
    edit: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" /></svg>,
    trash: <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>,
  };
  return icons[name] || null;
};

const ClientDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [projects, setProjects] = useState([]);
  const [orgSettings, setOrgSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadClientData();
  }, [id]);

  const loadClientData = async () => {
    try {
      setLoading(true);
      const [clientRes, projectsRes, orgRes] = await Promise.all([
        clientAPI.getById(id),
        projectAPI.getAll({ client: id }),
        settingsAPI.getOrganization()
      ]);
      setClient(clientRes.data.data);
      setProjects(projectsRes.data.data || []);
      setOrgSettings(orgRes.data.data);
    } catch (error) {
      console.error('Failed to load client data:', error);
    } finally {
      setLoading(false);
    }
  };

  const currencyCode = orgSettings?.baseCurrency || 'USD';
  const currencyLocale = currencyCode === 'INR' ? 'en-IN' : 'en-US';

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(currencyLocale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <Layout>
        <div className="marsh-client-details">
          <div className="marsh-loading">Analyzing client profile...</div>
        </div>
      </Layout>
    );
  }

  if (!client) {
    return (
      <Layout>
        <div className="marsh-client-details">
          <div className="marsh-error">Entity not found in records.</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="marsh-client-details">
        {/* Navigation Bar */}
        <div className="details-header-nav">
          <button className="marsh-btn-minimal" onClick={() => navigate('/clients')}>
            <Icon name="arrowLeft" size={18} color="#7c3aed" />
            <span>Portfolio</span>
          </button>
          <div className="nav-actions">
            <button className="marsh-btn-edit-round" onClick={() => navigate(`/clients/edit/${id}`)}>
              <Icon name="edit" size={18} />
              <span>Adjust</span>
            </button>
            <button className="marsh-btn-delete-round" onClick={async () => {
              if (window.confirm('Archive this client?')) {
                await clientAPI.delete(id);
                navigate('/clients');
              }
            }}>
              <Icon name="trash" size={18} />
            </button>
          </div>
        </div>

        {/* Hero Section */}
        <div className="client-hero-marsh">
          <div className="hero-avatar">
            {client.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
          </div>
          <div className="hero-content">
            <h1 className="hero-name">{client.name}</h1>
            <div className="hero-meta">
              <span className="industry-tag">{client.industry || 'General Business'}</span>
              <span className={`marsh-status-pill ${client.isActive ? 'active' : 'inactive'}`}>
                {client.isActive ? 'Active Partnership' : 'Archived'}
              </span>
            </div>
          </div>
        </div>

        {/* Info Blocks */}
        <div className="marsh-details-grid">
          <div className="marsh-info-block">
            <h3>Primary Contact</h3>
            <div className="info-entry">
              <Icon name="user" size={18} color="#94a3b8" />
              <div>
                <p className="entry-label">Representative</p>
                <p className="entry-value">{client.contactPerson || 'N/A'}</p>
              </div>
            </div>
            <div className="info-entry">
              <Icon name="mail" size={18} color="#94a3b8" />
              <div>
                <p className="entry-label">Contact Email</p>
                <p className="entry-value">{client.email}</p>
              </div>
            </div>
            {client.phone && (
              <div className="info-entry">
                <Icon name="phone" size={18} color="#94a3b8" />
                <div>
                  <p className="entry-label">Direct Line</p>
                  <p className="entry-value">{client.phone}</p>
                </div>
              </div>
            )}
            {client.location && (
              <div className="info-entry">
                <Icon name="mapPin" size={18} color="#94a3b8" />
                <div>
                  <p className="entry-label">Regional HQ</p>
                  <p className="entry-value">{client.location}</p>
                </div>
              </div>
            )}
          </div>

          <div className="marsh-info-block">
            <h3>Contract Logic</h3>
            <div className="info-entry">
              <Icon name="fileText" size={18} color="#94a3b8" />
              <div>
                <p className="entry-label">Engagement Model</p>
                <p className="entry-value">{client.contractType || 'Fixed'}</p>
              </div>
            </div>
            <div className="info-entry">
              <Icon name="currency" size={18} color="#94a3b8" />
              <div>
                <p className="entry-label">Total Contract Value</p>
                <p className="entry-value highlight">{formatCurrency(client.contractValue || 0)}</p>
              </div>
            </div>
            <div className="info-entry">
              <Icon name="calendar" size={18} color="#94a3b8" />
              <div>
                <p className="entry-label">Partnership Growth</p>
                <p className="entry-value">Since {formatDate(client.createdAt)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Project List */}
        <div className="marsh-projects-section">
          <div className="section-title-row">
            <h2>Active Deployments</h2>
            <span className="count-badge-large">{projects.length} Projects</span>
          </div>

          {projects.length === 0 ? (
            <div className="marsh-empty-projects">No active projects assigned.</div>
          ) : (
            <div className="marsh-project-list-vertical">
              {projects.map(project => (
                <div key={project._id} className="marsh-project-row">
                  <div className="project-main-info">
                    <p className="p-name">{project.name}</p>
                    <p className="p-cat">{project.category}</p>
                  </div>
                  <div className="project-budget-info">
                    <p className="p-label">Budget allocation</p>
                    <p className="p-val">{formatCurrency(project.budget || 0)}</p>
                  </div>
                  <div className="project-status-col">
                    <span className={`marsh-status-pill blue`}>
                      {project.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default ClientDetails;
